import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Shield, FileText, Zap, Mail, Loader2, Clock, Download, RefreshCw, AlertTriangle, CheckCircle, XCircle, Truck, Award, ExternalLink, Camera, Upload, MapPin, Edit2, Save, Lock, Share2 } from 'lucide-react';
import { ShareCertificateDialog } from '@/components/account/ShareCertificateDialog';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { toast } from 'sonner';
import { IMAGE_UPLOAD_ACCEPT } from '@/lib/uploadAccept';

const STATUS_LABELS = {
  order_received: 'Order Received', in_review: 'In Review',
  more_images_requested: 'More Images Requested', images_received: 'Images Received',
  authentic: 'Authentic', counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Complete', unable_to_determine: 'Unable to Determine',
  refunded: 'Refunded', revoked: 'Revoked',
};
const STATUS_COLORS = {
  authentic: { bg: 'rgba(14,107,93,0.10)', color: '#0E6B5D' },
  counterfeit: { bg: 'rgba(90,31,42,0.10)', color: '#5A1F2A' },
  appraisal_complete: { bg: 'rgba(14,107,93,0.10)', color: '#0E6B5D' },
  refunded: { bg: 'rgba(90,31,42,0.10)', color: '#5A1F2A' },
  revoked: { bg: 'rgba(90,31,42,0.10)', color: '#5A1F2A' },
  in_review: { bg: 'rgba(31,94,122,0.10)', color: '#1F5E7A' },
  more_images_requested: { bg: 'rgba(163,106,58,0.10)', color: '#A36A3A' },
};
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","PR"];

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [itemDocs, setItemDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingAddr, setEditingAddr] = useState(false);
  const [addrForm, setAddrForm] = useState({});
  const [savingAddr, setSavingAddr] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const fileRef = useRef(null);
  const uploadItemId = params.get('upload'); // from email deep link

  useSeoMeta({ seo_title: order ? `Order ${order.order_number}` : 'Order Detail' });

  const loadOrder = async () => {
    try {
      const r = await api.get(`/orders/${orderId}`);
      setOrder(r.data);
      if (r.data.shipping_address) setAddrForm(r.data.shipping_address);
      const docs = {};
      for (const item of (r.data.items || [])) {
        try { const d = await api.get(`/documents/by-item/${item.id}`); docs[item.id] = d.data; } catch {}
      }
      setItemDocs(docs);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { loadOrder(); }, [orderId]); // eslint-disable-line

  const saveAddress = async () => {
    setSavingAddr(true);
    try {
      await api.put(`/orders/${orderId}/shipping-address`, addrForm);
      toast.success('Address updated');
      setEditingAddr(false);
      await loadOrder();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingAddr(false); }
  };

  const handleFileUpload = async (itemId, fileList) => {
    // Aug 2026: accept multiple files at once. Callers pass either a
    // FileList (from the <input multiple>) or a single File so both
    // old and new call sites work.
    const arr = fileList instanceof FileList ? Array.from(fileList) : (fileList ? [fileList] : []);
    if (!arr.length) return;
    setUploading(itemId);
    try {
      const formData = new FormData();
      for (const f of arr) formData.append('files', f);
      const { data } = await api.post(
        `/orders/${orderId}/items/${itemId}/upload-images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const okCount = (data.uploaded_assets || []).length;
      const errCount = (data.errors || []).length;
      if (okCount && !errCount) {
        toast.success(okCount === 1 ? 'Photo uploaded!' : `${okCount} photos uploaded!`);
      } else if (okCount && errCount) {
        toast.success(`${okCount} uploaded, ${errCount} failed`);
      } else if (errCount) {
        const first = data.errors[0];
        toast.error(`${first.filename || 'file'}: ${first.error}`);
      } else {
        toast.success('Photo uploaded!');
      }
      await loadOrder();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!order) return <div className="text-center py-12"><p className="text-sm text-muted-foreground">Order not found.</p></div>;

  const shippingAddr = order.shipping_address;
  const addrLocked = order.shipping_address_locked;

  return (
    <div data-testid="order-detail-page">
      <Link to="/account/orders" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink mb-4"><ArrowLeft className="h-3.5 w-3.5" /> All Orders</Link>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-xl font-medium text-ink" data-testid="order-detail-number">{order.order_number}</h1>
          <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()} - ${(order.total_cents / 100).toFixed(2)}</p>
        </div>
        <Badge variant="outline" className="text-xs" style={STATUS_COLORS[order.status === 'paid' ? 'in_review' : order.status]}>
          {order.status === 'paid' ? 'Order Received' : (STATUS_LABELS[order.status] || order.status)}
        </Badge>
      </div>

      {order.status === 'refunded' && (
        <Card className="bg-red-50/50 border-red-200 mb-4"><CardContent className="p-4 flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-red-600 flex-shrink-0" /><div><p className="text-sm font-medium text-red-800">This order has been refunded</p></div>
        </CardContent></Card>
      )}

      {/* Items */}
      <div className="space-y-4 mb-6">
        {(order.items || []).map((item, idx) => {
          const statusColor = STATUS_COLORS[item.status] || {};
          const docs = itemDocs[item.id] || {};
          const cert = docs.certificate;
          const appraisal = docs.appraisal_report;
          const counterfeitDoc = docs.counterfeit_letter;
          const hasMailed = item.add_ons?.some(a => a.add_on_type === 'mailed_document');
          const needsUpload = item.status === 'more_images_requested';
          const isUploadTarget = uploadItemId === item.id;

          return (
            <Card key={idx} className={`bg-white border-stone/50 ${needsUpload ? 'border-l-4 border-l-orange-400' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {item.service_family === 'authentication' ? <Shield className="h-4 w-4" style={{ color: '#0E6B5D' }} /> : <FileText className="h-4 w-4" style={{ color: '#A36A3A' }} />}
                    <span className="text-sm font-medium text-ink">{item.service_name}</span>
                    <Badge variant="outline" className="text-[10px]">{item.category_name}</Badge>
                  </div>
                  <Badge variant="outline" className="text-[10px]" style={statusColor}>{STATUS_LABELS[item.status] || item.status}</Badge>
                </div>
                <p className="text-sm text-ink font-medium">{item.item_title || 'Untitled'}</p>
                {item.brand_name && <p className="text-xs text-muted-foreground">{item.brand_name}</p>}
                {item.serial_number && <p className="text-xs text-muted-foreground">SN: {item.serial_number}</p>}

                {item.add_ons && item.add_ons.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {item.add_ons.map((addon, i) => (
                      <Badge key={i} variant="outline" className={`text-[9px] ${addon.is_refunded ? 'line-through opacity-50' : ''}`}>
                        {addon.add_on_type === 'expedited' ? <><Zap className="h-3 w-3 mr-0.5" /> Expedited</> : <><Mail className="h-3 w-3 mr-0.5" /> Mailed Doc</>}
                        {addon.is_refunded && ' (refunded)'}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* More images requested — upload UI */}
                {needsUpload && (
                  <div className="mt-3 p-3 rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/30" data-testid={`reupload-${item.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Camera className="h-4 w-4" style={{ color: '#A36A3A' }} />
                      <span className="text-xs font-medium" style={{ color: '#A36A3A' }}>Additional Photos Requested</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">Our team needs more photos to continue reviewing this item. You can select multiple files at once.</p>
                    <input
                      type="file"
                      ref={isUploadTarget ? fileRef : null}
                      accept={IMAGE_UPLOAD_ACCEPT}
                      multiple
                      className="hidden"
                      id={`upload-${item.id}`}
                      onChange={e => handleFileUpload(item.id, e.target.files)}
                    />
                    <Button size="sm" className="gap-1.5 text-xs" onClick={() => document.getElementById(`upload-${item.id}`)?.click()} disabled={uploading === item.id} data-testid={`upload-photos-btn-${item.id}`}>
                      {uploading === item.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {uploading === item.id ? 'Uploading...' : 'Upload Photos'}
                    </Button>
                  </div>
                )}

                {hasMailed && (() => {
                  const mf = order.mailed_fulfillments?.[item.id];
                  const sl = mf?.status === 'shipped' ? 'Shipped' : mf?.status === 'ready_to_print' ? 'Ready to Ship' : mf?.status === 'cancelled' ? 'Cancelled' : 'Awaiting Review';
                  const sc = mf?.status === 'shipped' ? '#0E6B5D' : mf?.status === 'cancelled' ? '#5A1F2A' : '#A36A3A';
                  return <div className="mt-3 p-3 rounded-lg" style={{ background: 'hsl(var(--bg-paper-2))' }}><div className="flex items-center gap-2 text-xs"><Truck className="h-3.5 w-3.5" style={{ color: sc }} /><span style={{ color: sc }} className="font-medium">Mailed Document: {sl}</span></div></div>;
                })()}

                {cert && (
                  <div className="mt-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/30">
                    <div className="flex items-center gap-2 mb-2"><Award className="h-4 w-4" style={{ color: '#0E6B5D' }} /><span className="text-xs font-medium" style={{ color: '#0E6B5D' }}>Certificate of Authenticity</span><span className="font-mono text-[10px] text-muted-foreground ml-auto">{cert.certificate_code}</span></div>
                    <div className="flex gap-2">
                      {cert.files?.find(f => f.file_type === 'pdf') && (() => { const pdf = cert.files.find(f => f.file_type === 'pdf'); return <a href={pdf.url || `${API_BASE}/storage/files/${pdf.storage_key}`} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1.5"><Download className="h-3 w-3" /> PDF</Button></a>; })()}
                      <Link to={`/verify?code=${cert.certificate_code}`}><Button size="sm" variant="outline" className="text-xs gap-1.5" data-testid={`view-certificate-${item.id}`}><ExternalLink className="h-3 w-3" /> View Certificate</Button></Link>
                      {!cert.is_revoked && (
                        <Button size="sm" variant="outline" className="text-xs gap-1.5" data-testid={`share-certificate-${item.id}`}
                          onClick={() => setShareTarget({ itemId: item.id, certCode: cert.certificate_code, itemTitle: item.item_title })}>
                          <Share2 className="h-3 w-3" /> Share
                        </Button>
                      )}
                    </div>
                    {cert.is_revoked && <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: '#5A1F2A' }}><XCircle className="h-3 w-3" /> Revoked</div>}
                  </div>
                )}

                {appraisal && (
                  <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/30">
                    <div className="flex items-center gap-2 mb-2"><FileText className="h-4 w-4" style={{ color: '#A36A3A' }} /><span className="text-xs font-medium" style={{ color: '#A36A3A' }}>Appraisal Report</span>{appraisal.appraised_value_cents && <span className="text-xs font-semibold ml-auto">${(appraisal.appraised_value_cents / 100).toLocaleString()}</span>}</div>
                    {(appraisal.url || appraisal.storage_key) && <a href={appraisal.url || `${API_BASE}/storage/files/${appraisal.storage_key}`} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1.5"><Download className="h-3 w-3" /> PDF</Button></a>}
                  </div>
                )}

                {counterfeitDoc && (
                  <div className="mt-3 p-3 rounded-lg border border-red-200 bg-red-50/30">
                    <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" style={{ color: '#5A1F2A' }} /><span className="text-xs font-medium" style={{ color: '#5A1F2A' }}>Authentication Results</span></div>
                    {(counterfeitDoc.url || counterfeitDoc.storage_key) && <a href={counterfeitDoc.url || `${API_BASE}/storage/files/${counterfeitDoc.storage_key}`} target="_blank" rel="noopener noreferrer" className="no-underline"><Button size="sm" variant="outline" className="text-xs gap-1.5"><Download className="h-3 w-3" /> Document</Button></a>}
                  </div>
                )}

                {item.assets && item.assets.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {item.assets.slice(0, 8).map((a, i) => <div key={i} className="h-14 w-14 rounded-lg overflow-hidden border border-stone/30"><img src={a.url || `${API_BASE}/storage/files/${a.storage_key}`} alt={a.slot_key} className="w-full h-full object-cover" loading="lazy" decoding="async" /></div>)}
                    {item.assets.length > 8 && <div className="h-14 w-14 rounded-lg flex items-center justify-center bg-stone/10 text-xs text-muted-foreground">+{item.assets.length - 8}</div>}
                  </div>
                )}

                <Separator className="my-3" />
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Item Total</span><span className="font-medium text-ink">${(item.total_cents / 100).toFixed(2)}</span></div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Shipping Address — editable until shipped */}
      {shippingAddr && (
        <Card className="bg-white border-stone/50 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Shipping Address</h3>
              {addrLocked
                ? <Badge variant="outline" className="text-[9px] gap-1"><Lock className="h-2.5 w-2.5" /> Locked (shipped)</Badge>
                : !editingAddr && <Button size="sm" variant="outline" className="h-6 text-[10px] gap-0.5" onClick={() => setEditingAddr(true)} data-testid="edit-addr-btn"><Edit2 className="h-2.5 w-2.5" /> Edit</Button>
              }
            </div>
            {editingAddr ? (
              <div className="space-y-2">
                <div><Label className="text-xs">Street</Label><Input value={addrForm.line_1 || ''} onChange={e => setAddrForm({...addrForm, line_1: e.target.value})} className="mt-0.5 h-8 text-xs" /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">City</Label><Input value={addrForm.city || ''} onChange={e => setAddrForm({...addrForm, city: e.target.value})} className="mt-0.5 h-8 text-xs" /></div>
                  <div><Label className="text-xs">State</Label>
                    <Select value={addrForm.state || ''} onValueChange={v => setAddrForm({...addrForm, state: v})}>
                      <SelectTrigger className="mt-0.5 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">ZIP</Label><Input value={addrForm.postal_code || ''} onChange={e => setAddrForm({...addrForm, postal_code: e.target.value})} className="mt-0.5 h-8 text-xs" /></div>
                </div>
                <div className="flex gap-2"><Button size="sm" className="text-xs gap-1" onClick={saveAddress} disabled={savingAddr}><Save className="h-3 w-3" /> Save</Button><Button size="sm" variant="outline" className="text-xs" onClick={() => setEditingAddr(false)}>Cancel</Button></div>
              </div>
            ) : (
              <p className="text-xs text-ink">{shippingAddr.line_1}{shippingAddr.line_2 ? `, ${shippingAddr.line_2}` : ''}<br />{shippingAddr.city}, {shippingAddr.state} {shippingAddr.postal_code}</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-stone/50"><CardContent className="p-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${(order.subtotal_cents / 100).toFixed(2)}</span></div>
          {order.discount_cents > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-${(order.discount_cents / 100).toFixed(2)}</span></div>}
          <Separator /><div className="flex justify-between font-semibold"><span>Total</span><span>${(order.total_cents / 100).toFixed(2)}</span></div>
        </div>
      </CardContent></Card>
      <ShareCertificateDialog target={shareTarget} onClose={() => setShareTarget(null)} />
    </div>
  );
}
